import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Check, ArrowUpRight, Phone } from "lucide-react";
import Reviews from "@/components/Reviews";
import { services, getService } from "@/data/services";
import { servicePriceMap, findPrestation, catalogItemForSlug, priceLabelForSlug } from "@/data/pricing";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = getService(slug);

  useEffect(() => {
    document.title = service
      ? `${service.title} — NBPerf Reprogrammation moteur`
      : "NBPerf — Reprogrammation moteur";
    return () => {
      document.title = "NBPerf — Reprogrammation moteur E85 & diesel";
    };
  }, [service]);

  if (!service) {
    return (
      <main className="min-h-screen flex flex-col items-center justify-center px-5" data-testid="service-not-found">
        <h1 className="font-display text-4xl sm:text-6xl text-white">Prestation introuvable</h1>
        <Link
          to="/#services"
          className="mt-8 clip-angle inline-flex items-center gap-2 border border-[#6EB000] text-[#6EB000] px-8 py-4 text-sm uppercase tracking-widest hover:bg-[#6EB000]/10 transition-colors"
          data-testid="service-not-found-back"
        >
          <ArrowLeft className="w-4 h-4" /> Tous les services
        </Link>
      </main>
    );
  }

  const Icon = service.icon;
  const others = services.filter((s) => s.slug !== slug).slice(0, 6);
  const catalogItem = catalogItemForSlug(slug);
  const pid = servicePriceMap[slug];
  const cfgParam = pid && pid !== "devis" ? (findPrestation(pid) ? `prestation=${pid}` : `option=${pid}`) : "";
  const cfgUrl = `/configurateur${cfgParam ? `?${cfgParam}` : ""}`;
  const needsConfirm = !catalogItem || catalogItem.confirm || catalogItem.price == null;

  return (
    <main className="pt-16" data-testid="service-detail-page">
      <section className="relative py-16 sm:py-24 border-b border-white/10 overflow-hidden">
        <span className="absolute -top-10 right-0 font-display text-[40vw] sm:text-[18rem] leading-none text-white/[0.03] select-none pointer-events-none">
          {service.title.split(" ")[0]}
        </span>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 relative">
          <Link
            to="/#services"
            className="inline-flex items-center gap-2 text-white/50 hover:text-[#6EB000] text-sm uppercase tracking-widest transition-colors"
            data-testid="service-back-link"
          >
            <ArrowLeft className="w-4 h-4" /> Tous les services
          </Link>
          <motion.p
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="font-script text-2xl sm:text-3xl mt-10"
            style={{ color: service.accent }}
            data-testid="service-script"
          >
            {service.script}
          </motion.p>
          <div className="overflow-hidden pb-1">
            <motion.h1
              initial={{ y: "110%" }}
              animate={{ y: 0 }}
              transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
              className="font-display text-5xl sm:text-7xl lg:text-8xl text-white leading-[0.95]"
              data-testid="service-title"
            >
              {service.title}
            </motion.h1>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mt-8 flex items-center gap-4"
          >
            <span className="flex items-center justify-center w-14 h-14 border" style={{ borderColor: service.accent }}>
              <Icon className="w-7 h-7" style={{ color: service.accent }} strokeWidth={1.6} />
            </span>
            <p className="text-white/50 text-xs uppercase tracking-widest">NBPerf — Nicolas Bourillon · Passionné depuis 1998</p>
          </motion.div>
        </div>
      </section>

      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="font-display text-3xl sm:text-4xl text-white" data-testid="service-intro-heading">
              En quoi ça consiste<span style={{ color: service.accent }}> ?</span>
            </h2>
            <p className="text-white/70 mt-6 leading-relaxed" data-testid="service-intro-text">{service.intro}</p>
            {service.disclaimer && (
              <p className="font-script text-xl sm:text-2xl text-[#F5A623] mt-8 border-l-2 border-[#F5A623] pl-4" data-testid="service-disclaimer">
                {service.disclaimer}
              </p>
            )}
            <div className="mt-12">
              <h3 className="uppercase tracking-widest text-white/50 text-sm mb-6">Comment ça se passe</h3>
              <div className="space-y-4" data-testid="service-steps">
                {service.steps.map((step, i) => (
                  <div key={i} className="flex items-start gap-5 border border-[#454C50] p-5">
                    <span className="font-display text-2xl text-outline leading-none mt-0.5">0{i + 1}</span>
                    <p className="text-white/70 text-sm sm:text-base">{step}</p>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.15 }}
          >
            <div className="bg-[#343A3D] border border-[#454C50] p-8 sm:p-10 glow-green">
              <h3 className="font-display text-2xl sm:text-3xl text-white">Les bénéfices</h3>
              <ul className="mt-8 space-y-5" data-testid="service-benefits">
                {service.benefits.map((b, i) => (
                  <li key={i} className="flex items-start gap-4">
                    <span
                      className="shrink-0 flex items-center justify-center w-7 h-7 rounded-full border"
                      style={{ borderColor: service.accent }}
                    >
                      <Check className="w-4 h-4" style={{ color: service.accent }} />
                    </span>
                    <span className="text-white/80 text-sm sm:text-base">{b}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8 border border-[#6EB000]/40 bg-[#6EB000]/5 p-5" data-testid="service-price-block">
                <p className="text-[11px] uppercase tracking-widest text-white/50">Tarif officiel NBPerf</p>
                <p className="font-display text-3xl text-[#6EB000] mt-1" data-testid="service-price">{priceLabelForSlug(slug)}</p>
                {needsConfirm && (
                  <p className="text-xs text-[#F5A623] mt-2">* Confirmation NBPerf nécessaire avant paiement</p>
                )}
              </div>
              <Link
                to={cfgUrl}
                className="clip-angle mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#6EB000] text-black font-bold px-8 py-4 text-sm uppercase tracking-widest hover:bg-[#5A9000] active:scale-95 transition-all duration-200"
                data-testid="service-cta-quote"
              >
                Configurer &amp; payer en ligne
                <ArrowUpRight className="w-4 h-4" />
              </Link>
              <a
                href="tel:+33781901248"
                className="mt-4 w-full inline-flex items-center justify-center gap-2 border border-[#454C50] hover:border-[#6EB000] text-white/80 px-8 py-4 text-sm uppercase tracking-widest transition-colors"
                data-testid="service-cta-phone"
              >
                <Phone className="w-4 h-4 text-[#6EB000]" /> 07 81 90 12 48
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Reviews />

      <section className="py-16 border-t border-white/10 bg-[#2E3336]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <h3 className="font-display text-2xl sm:text-3xl text-white mb-8">Autres prestations</h3>
          <div className="flex flex-wrap gap-3" data-testid="service-related">
            {others.map((s) => (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                className="px-5 py-3 border border-[#454C50] hover:border-[#6EB000] text-white/70 hover:text-[#6EB000] text-sm uppercase tracking-widest transition-colors"
                data-testid={`service-related-${s.slug}`}
              >
                {s.title}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
